import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Droplet, Sun, Eye, PartyPopper, Loader2 } from 'lucide-react';
import { TeamBApi, ApiError, formatDate, type BCareAction } from '@ai-tiaozs/shared';
import { SafeImage } from '@/components/SafeImage';

const MAIN_TASK_ACTION = '完成今日任务' as const;

const EXTRA_ACTIONS: { action: BCareAction; label: string; icon: typeof Droplet }[] = [
  { action: '浇水', label: '我还浇了水', icon: Droplet },
  { action: '调整光照', label: '我挪了位置', icon: Sun },
  { action: '继续观察', label: '我又看了一遍', icon: Eye },
];

interface CareTaskPanelProps {
  plantId: string;
  plantName: string;
  observationId?: string | null;
  todayAction: string;
  reminder?: string;
  photoUrl?: string | null;
  photoKey?: string | null;
  embedded?: boolean;
  onCompleted?: () => void;
}

/**
 * 「今天只做 1 件事」— 孩子能看懂的执行确认(完成后持久化,再次打开仍显示已完成)
 */
export function CareTaskPanel({
  plantId,
  plantName,
  observationId,
  todayAction,
  reminder,
  photoUrl,
  photoKey,
  embedded = false,
  onCompleted,
}: CareTaskPanelProps) {
  const [loading, setLoading] = useState(Boolean(observationId));
  const [submitting, setSubmitting] = useState(false);
  const [mainDone, setMainDone] = useState(false);
  const [completedAt, setCompletedAt] = useState<number | null>(null);
  const [extrasDone, setExtrasDone] = useState<Set<BCareAction>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const loadStatus = useCallback(async () => {
    if (!observationId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { actions } = await TeamBApi.listCareActions(observationId);
      const main = actions.find((a) => a.action === MAIN_TASK_ACTION);
      // 兼容旧版:仅点过主按钮时会存成「继续观察」
      const legacyMain =
        !main &&
        actions.length === 1 &&
        actions[0]?.action === '继续观察';
      const done = main ?? (legacyMain ? actions[0] : null);
      if (done) {
        setMainDone(true);
        setCompletedAt(done.performedAt ?? done.createdAt);
      } else {
        setMainDone(false);
        setCompletedAt(null);
      }
      setExtrasDone(
        new Set(
          actions
            .map((a) => a.action as BCareAction)
            .filter((a) => EXTRA_ACTIONS.some((e) => e.action === a)),
        ),
      );
    } catch {
      // 读取失败时不阻断做任务
    } finally {
      setLoading(false);
    }
  }, [observationId]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  const submitAction = async (action: BCareAction, note: string) => {
    setSubmitting(true);
    setError(null);
    try {
      await TeamBApi.careAction({
        plantId,
        observationId: observationId ?? undefined,
        action,
        performed: true,
        note,
      });
      return true;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : '记录失败,请重试');
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  const onMainDone = async () => {
    const ok = await submitAction(MAIN_TASK_ACTION, todayAction);
    if (ok) {
      setMainDone(true);
      setCompletedAt(Date.now());
      onCompleted?.();
    }
  };

  const onExtra = async (action: BCareAction, label: string) => {
    if (extrasDone.has(action)) return;
    const ok = await submitAction(action, label);
    if (ok) {
      setExtrasDone((prev) => new Set(prev).add(action));
      onCompleted?.();
    }
  };

  if (loading) {
    return (
      <p className="flex items-center justify-center gap-1 py-4 text-xs text-slate-500">
        <Loader2 className="h-3 w-3 animate-spin" />
        看看今天有没有做过…
      </p>
    );
  }

  if (mainDone) {
    return (
      <section className="card space-y-3 border-emerald-200 bg-emerald-50/80 text-center">
        <PartyPopper className="mx-auto h-10 w-10 text-emerald-600" />
        <span className="badge bg-emerald-600 text-white">✅ 这条任务已完成</span>
        <h2 className="text-lg font-bold text-emerald-800">今天的事已经做完啦!</h2>
        <div className="rounded-lg bg-white/80 px-3 py-2 text-left text-sm text-slate-700">
          <p className="text-[11px] text-slate-500">你完成的是:</p>
          <p className="mt-1 font-medium">{todayAction}</p>
        </div>
        {completedAt && (
          <p className="text-xs text-slate-500">完成时间:{formatDate(completedAt)}</p>
        )}
        {extrasDone.size > 0 && (
          <p className="text-xs text-slate-600">
            还记录了:{[...extrasDone].join('、')}
          </p>
        )}
        <p className="text-xs text-slate-500">明天再来拍一张照片,看看植物有没有变好</p>
        <Link
          to={`/timeline?plantId=${plantId}`}
          className="btn-primary inline-flex w-full justify-center"
        >
          去看养护日记 →
        </Link>
      </section>
    );
  }

  return (
    <section className="card space-y-4 border-teamB-primary/20 bg-gradient-to-b from-teamB-primary/5 to-white">
      {!embedded && (
        <header className="flex items-center gap-3">
          <SafeImage
            team="b"
            imageUrl={photoUrl}
            imageKey={photoKey}
            alt={plantName}
            className="h-14 w-14 rounded-md object-cover ring-2 ring-teamB-primary/20"
            placeholderClassName="flex h-14 w-14 items-center justify-center rounded-md bg-teamB-light/40 text-xl"
          />
          <div>
            <p className="text-xs text-slate-500">给这株植物</p>
            <p className="font-bold text-teamB-primary">{plantName}</p>
          </div>
        </header>
      )}

      <div>
        <p className="text-center text-sm font-semibold text-slate-700">
          {embedded ? '🎯 今天只做这 1 件事' : '🎯 今天你要做这件事'}
        </p>
        <div className="mt-2 rounded-xl border-2 border-teamB-primary/30 bg-white px-4 py-5 text-center">
          <p className="text-lg font-bold leading-relaxed text-slate-800">{todayAction}</p>
        </div>
        {reminder && (
          <p className="mt-2 text-center text-xs text-slate-500">💡 {reminder}</p>
        )}
      </div>

      <button
        type="button"
        onClick={() => void onMainDone()}
        disabled={submitting}
        className="btn-primary flex w-full items-center justify-center gap-2 py-3 text-base"
      >
        <Check className="h-5 w-5" />
        {submitting ? '正在记录…' : '我做完了!'}
      </button>

      <div className="border-t border-slate-100 pt-3">
        <p className="text-center text-[11px] text-slate-400">还顺便做了别的?(可不选)</p>
        <div className="mt-2 flex flex-wrap justify-center gap-2">
          {EXTRA_ACTIONS.map(({ action, label, icon: Icon }) => {
            const done = extrasDone.has(action);
            return (
              <button
                key={action}
                type="button"
                disabled={submitting || done}
                onClick={() => void onExtra(action, label)}
                className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs ${
                  done
                    ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-teamB-primary/40'
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {done ? '已记录 ✓' : label}
              </button>
            );
          })}
        </div>
      </div>

      {error && <p className="text-center text-xs text-teamA-danger">{error}</p>}
    </section>
  );
}
